import type {
  GridAlignment,
  GridColDef,
  GridColTypeDef,
  GridColumnGroupingModel,
  GridColumnHeaderParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { getDimensionTypeProps } from '@/widgets/DataTable';
import type {
  ColumnsConfig,
  MetricColumnDataType,
  MetricDataFormatType,
  DimensionColumnDataType,
} from '@/entities/columnsConfig';
import { toFormatType } from '@/entities/columnsConfig';
import { selectCurrency } from '@/entities/session';
import { DEFAULT_COLUMN_WIDTH } from '@/shared/constants';
import { useNumberFormatter } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import type { MinMaxData } from '@/shared/ui';
import { DIMENSION_MIN_WIDTH, ProgressCell } from '@/shared/ui';
import { CohortAgeHeader } from '../CohortAgeHeader/CohortAgeHeader';
import { DimensionTableHeader } from '../DimensionTableHeader/DimensionTableHeader';
import { DATA_COLUMN_MIN_WIDTH } from './constants';

interface DataTableProps {
  columns: GridColDef[];
  columnGroupingModel: GridColumnGroupingModel;
}

interface Params {
  columnsConfig: ColumnsConfig;
  columnsWidth: Record<string, number | undefined>;
  dimensions: string[];
  metric: string;
  dataColumns: string[];
  minMax?: MinMaxData[string];
}

export const useTableProps = ({
  columnsConfig,
  columnsWidth,
  dimensions,
  metric,
  dataColumns,
  minMax,
}: Params): DataTableProps => {
  const userCurrency = useAppSelector(selectCurrency);
  const formattersMap = useNumberFormatter(userCurrency);
  const columns: GridColDef[] = useMemo(
    () => {
      const metricConfig = columnsConfig[metric];
      const columnsDefs: GridColDef[] = [
        ...dimensions.map((dimension, ind) => {
          const { name, description, type } = columnsConfig[dimension];

          return {
            ...getDimensionTypeProps(type as DimensionColumnDataType, ind),
            headerName: name,
            description: description,
            field: dimension,
            minWidth: DIMENSION_MIN_WIDTH,
            width: columnsWidth[dimension] || DEFAULT_COLUMN_WIDTH,
            headerClassName: 'dimensionHeader',
            renderHeader: ({ colDef }: GridColumnHeaderParams) => {
              if (!colDef) {
                return null;
              }

              return (
                <DimensionTableHeader
                  field={colDef.field}
                  name={colDef.headerName ?? colDef.field}
                  description={colDef.description}
                />
              );
            },
          };
        }),
      ];

      if (!metricConfig) {
        return columnsDefs;
      }

      const metricFormatter =
        formattersMap[metricConfig.type as MetricColumnDataType][
          toFormatType(metricConfig.dataType as MetricDataFormatType)
        ];

      return columnsDefs.concat([
        ...dataColumns.map((column) => {
          return {
            ...getMetricTypeProps({
              metric,
              formatter: metricFormatter,
              minMax,
            }),
            field: column,
            minWidth: DATA_COLUMN_MIN_WIDTH,
            width: columnsWidth[column] || DEFAULT_COLUMN_WIDTH,
            disableReorder: true,
            align: 'right' as GridAlignment,
            headerClassName: 'metricHeader',
            cellClassName: 'metricCell',
            renderHeader: ({ colDef }: GridColumnHeaderParams) => {
              if (!colDef) {
                return null;
              }

              return <CohortAgeHeader cohortAge={colDef.field} />;
            },
          };
        }),
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dimensions, metric, dataColumns, columnsConfig, columnsWidth],
  );
  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'Dimensions',
      children: dimensions.map((dimension) => {
        return {
          field: dimension,
        };
      }),
      renderHeaderGroup: () => null,
      headerClassName: 'columnGroup',
    },
    {
      groupId: 'Dates',
      children: dataColumns.map((column) => {
        return {
          field: column,
        };
      }),
      renderHeaderGroup: () => null,
      headerClassName: 'columnGroup',
    },
  ];

  return {
    columns,
    columnGroupingModel,
  };
};

export const getMetricTypeProps = ({
  metric,
  minMax,
  formatter,
}: {
  metric: string;
  minMax?: MinMaxData[string];
  formatter: (v: number) => string;
}): GridColTypeDef => {
  return {
    type: 'number',
    renderCell: (params) => {
      const { field, row } = params;
      const { min, max } = minMax || {};

      if (row.isTotal) {
        return formatter(params.value);
      }

      const value = row[metric]?.[field];

      return (
        <ProgressCell min={min} max={max} value={value}>
          {formatter(value)}
        </ProgressCell>
      );
    },
  };
};
